import { useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, FreeMode, Mousewheel, Scrollbar } from 'swiper/modules';

import Heading from '@components/ui/Heading';
import ProductLineItem from '@components/algolia/ProductLineCarousel/ProductLineItem';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import { ProductLineCarouselType } from '@adapters/prismic/types/plp';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';

import styles from './ProductLineCarousel.module.scss';

export interface ProductLineCarouselProps {
  title?: string;
  productLineCarousel: ProductLineCarouselType;
  headerRef?: React.RefObject<HTMLElement | null>;
}

const ProductLineCarousel = ({
  title,
  productLineCarousel,
  headerRef,
}: ProductLineCarouselProps) => {
  const { items, heading, imageRatio } = productLineCarousel;
  const { refine } = useRefinementList({
    attribute: AlgoliaFacets.Collection,
    limit: 100,
  });
  const { indexUiState } = useInstantSearch();

  const refinedCollections = useMemo(
    () => indexUiState.refinementList?.[AlgoliaFacets.Collection] ?? [],
    [indexUiState.refinementList]
  );

  const selectedIndex = useMemo(() => {
    const index =
      items?.findIndex(
        (item: any) =>
          !!item?.product_line_name &&
          refinedCollections.includes(item.product_line_name)
      ) ?? -1;
    return index === -1 ? null : index;
  }, [items, refinedCollections]);

  const [, setSwiperSize] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleItemSelect = (index: number, productLineName: string) => {
    const isCurrentlySelected =
      !!productLineName && refinedCollections.includes(productLineName);

    refinedCollections.forEach((value) => refine(value));

    if (productLineName && !isCurrentlySelected) {
      refine(productLineName);

      if (headerRef?.current) {
        const yOffset = -190;
        const y =
          headerRef.current.getBoundingClientRect().top +
          window.scrollY +
          yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      }
    }
  };

  const slides = items?.map((item: any, index: number) => {
    return (
      <SwiperSlide
        key={`product-line-carousel-${item?.product_line_name}}`}
        className={styles.swiperSlide}
      >
        <ProductLineItem
          item={item}
          index={index}
          isSelected={selectedIndex === index}
          onSelect={handleItemSelect}
          imageRatio={imageRatio}
          carouselTitle={title}
        />
      </SwiperSlide>
    );
  });

  return (
    <div className={styles.container} ref={containerRef}>
      {title && (
        <div className={styles.titleContainer}>
          <Heading semanticLevel="h1" styleLevel="1">
            {title}
          </Heading>
        </div>
      )}
      <div className={styles.titleContainer}>
        <p>{heading}</p>
      </div>
      <Swiper
        modules={[A11y, FreeMode, Scrollbar, Mousewheel]}
        className={styles.swiper}
        direction={'horizontal'}
        slidesPerView={1.75}
        breakpoints={{
          400: {
            slidesPerView: 2.25,
          },
          480: {
            slidesPerView: 2.75,
          },
          704: {
            slidesPerView: 3.25,
          },
          901: {
            slidesPerView: 3.75,
          },
          1061: {
            slidesPerView: 5.25,
          },
        }}
        spaceBetween={12}
        freeMode={true}
        grabCursor={true}
        onResize={(swiper) => {
          swiper.updateSize();
          setSwiperSize(swiper.width);
        }}
        watchSlidesProgress={true}
      >
        {slides}
      </Swiper>
    </div>
  );
};

export default ProductLineCarousel;
